import { isEmpty, isEqual } from 'lodash';
import type { SagaReturnType } from 'redux-saga/effects';
import {
  cancel,
  delay,
  put,
  select,
  take,
  takeLatest,
  call,
} from 'redux-saga/effects';
import { replace } from 'connected-react-router';
import type { TourInfo } from '@lt/api/dist/methods/packages/types';
import { getIsFeatureHasAnOption } from 'src/utils/featureToggle';
import { EXPERIMENT_NAMES } from 'src/components/Experiment';
import { STATUS_INTERVAL } from 'src/constants';
import { INIT_APP } from 'src/store/view/constants';
import { routeTransitionEndAction } from 'src/routes/Checkout/store/actions/view';
import { setCurrentPrice } from 'src/routes/Checkout/store/reducers/actualization';
import { createPackageSuccess } from 'src/routes/Checkout/store/actions/package';
import {
  getActualizeAlternativesStatus,
  getAltTourById,
  altActualizationSessionIdSelector,
  getCheapestPackagePriceWithAlternatives,
  isAltActualizationDoneSelector,
  altTourAmountSelector,
  servicesToUpgradePriceInfoSelector,
  getAlternativeToursIds,
  getProcessedAltToursCount,
  initialToursSelector,
  getAlternativeTours,
  toursAnalyticsSelector,
} from './selectors';
import { api } from '../../../../../../api';
import {
  getPackageId,
  getRequestId,
  getTourId,
  getBookingUrl,
  getNetPrice,
  getOperator,
  getPreviousPrice,
  getMedicalInsurance,
  getTransfer,
  getArchivedAt,
  getLabels,
  getInstantConfirm,
  getOperatorId,
} from '../../../../store/selectors/package';
import {
  changePackage,
  fetchAlternatives,
  selectFlightWithAlternatives,
  setActualizeAlternativesInfo,
  changePackageIdSuccess,
  setPreviousCheapestPackagePrice,
  setAltTour,
  setIsAltActualizationDone,
  setUpgradedServices,
  setIsChangingPackage,
  sendAltActualizationAnalytics,
  sendInitAltActualizationAnalytics,
  setPreviousPackageId,
  setPreviousOperatorId,
} from './slice';
import {
  fetchFlightsAction,
  flightsSetAction,
  fligthsFetchingDoneAction,
} from '../flights/slice';

import { selectFlight as selectFlightAction } from '../../../../store/actions/order';
import { setAnalytics } from '../../../../store/actions/analytics';
import { getIsHomePath, getPath } from '../../../../store/selectors/router';

import type {
  ActualizeAlternativesSlice,
  actualizeAltResponse,
  packageDetailsResponse,
} from './types';

import {
  getOriginalPackageOrderBonuses,
  getOriginalPackageOrderFuelFreeBonuses,
} from '../../../../store/selectors/order';
// eslint-disable-next-line import/no-cycle
import { getOriginalPackageExtras } from '../../../../store/selectors/services';
import {
  getActualizationSuccess,
  getLastUpdated,
} from '../../../../store/selectors/actualization';

import { similarPackagesStartAction } from '../../../../store/actions/similar-packages';
import type { PreselectedFilters } from '../flights-filters';
import { preselectedFiltersSelector } from '../flights-filters';
import {
  flightsCountSelector,
  selectCurrentFlightPrice,
} from '../flights/selectors';

/**
 * Запускает актуализацию соседних рейсов
 */
function* handleInitAltActualization() {
  const isArchived = yield select(getArchivedAt);
  // A/B TESTING
  const { NAME, OPTIONS } = EXPERIMENT_NAMES.ACTUALIZE_ALTERNATIVES;
  const allowAlternatives =
    getIsFeatureHasAnOption(NAME, OPTIONS.ON) && !isArchived;
  if (!allowAlternatives) return;
  // A/B TESTING
  try {
    const package_id = yield select(getPackageId);

    const response: actualizeAltResponse =
      yield api.postPackagesActualizeAlternatives({
        package_id,
      });

    yield put(setActualizeAlternativesInfo(response));

    const initTours = yield select(initialToursSelector);

    yield put(sendInitAltActualizationAnalytics({ initTours }));

    const altTourIds = yield select(getAlternativeToursIds);

    if (!isEmpty(altTourIds)) {
      yield put(fetchFlightsAction());
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
  }
}

/**
 * Перезапускает актуализацию соседних рейсов
 */
function* handleRestartActualizeAlternatives() {
  yield delay(STATUS_INTERVAL);

  yield put(fetchAlternatives());
}

function* handleFinishActualizeAlternatives() {
  yield put(setIsAltActualizationDone(true));

  const origActualizationDone = yield select(getLastUpdated);
  const anyFlightsFetched = Boolean(yield select(flightsCountSelector));
  if (origActualizationDone && anyFlightsFetched) {
    yield put(fligthsFetchingDoneAction());
  }

  const origActualizationSucceed = yield select(getActualizationSuccess);
  const altTours: ActualizeAlternativesSlice['alternative_tours'] =
    yield select(getAlternativeTours);
  const hasAnyAltTours = yield select(altTourAmountSelector);
  const preselectedFilters: PreselectedFilters = yield select(
    preselectedFiltersSelector,
  );
  const hasAltToursWithPreselectedOperators = !!altTours.filter((tour) =>
    preselectedFilters.operators.includes(tour.operator_info.id),
  ).length;

  const shouldStartSimilar =
    !origActualizationSucceed &&
    ((preselectedFilters.operators.length &&
      !hasAltToursWithPreselectedOperators) ||
      (!preselectedFilters.operators.length && !hasAnyAltTours));

  if (shouldStartSimilar) {
    yield put(similarPackagesStartAction());
  }
}

/**
 * Вызываем ручку перелетов и ждем пока они придут
 */
function* fetchAltFlights() {
  yield put(fetchFlightsAction());
  yield take(flightsSetAction.type);
}

/**
 * Актуализация соседних рейсов
 */
function* handleActualizeAlternatives() {
  const isAltActualizationDone: SagaReturnType<
    typeof isAltActualizationDoneSelector
  > = yield select(isAltActualizationDoneSelector);
  const isArchived = yield select(getArchivedAt);
  if (isAltActualizationDone || isArchived) {
    yield cancel();
  }
  // A/B TESTING
  const { NAME, OPTIONS } = EXPERIMENT_NAMES.ACTUALIZE_ALTERNATIVES;
  const allowAlternatives = getIsFeatureHasAnOption(NAME, OPTIONS.ON);
  if (!allowAlternatives) return;
  // A/B TESTING
  try {
    const package_id = yield select(getPackageId);
    const alternatives_actualization_session_id = yield select(
      altActualizationSessionIdSelector,
    );

    const previousAltTourIds = yield select(getAlternativeToursIds);
    const prevProcessedToursCount = yield select(getProcessedAltToursCount);
    const prevToursState = yield select(toursAnalyticsSelector);

    const response: actualizeAltResponse =
      yield api.postPackagesActualizeAlternatives({
        package_id,
        alternatives_actualization_session_id,
      });

    yield put(setActualizeAlternativesInfo(response));

    const newProcessedToursCount = yield select(getProcessedAltToursCount);
    const newToursState = yield select(toursAnalyticsSelector);

    if (newProcessedToursCount > prevProcessedToursCount) {
      yield put(
        sendAltActualizationAnalytics({ prevToursState, newToursState }),
      );
    }

    const cheapestPackagePrice = yield select(
      getCheapestPackagePriceWithAlternatives,
    );

    yield put(setPreviousCheapestPackagePrice(cheapestPackagePrice));

    const newAltTourIds = yield select(getAlternativeToursIds);

    if (!isEqual(newAltTourIds, previousAltTourIds)) {
      yield call(fetchAltFlights);
    }

    const actualizeStatus = yield select(getActualizeAlternativesStatus);

    if (actualizeStatus === 'ready') {
      yield call(handleFinishActualizeAlternatives);
    } else {
      yield call(handleRestartActualizeAlternatives);
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
  }
}

/**
 * Сага, срабатывающая перед прокидыванием события SELECT_FLIGHT:
 * Проверяет, не является ли рейс из альтернативного пакета
 * Если из альтернативного - то обновляем инфу по пакету на альтернативную
 */
function* handleSelectFlightWithAlternatives({
  payload,
}: ReturnType<typeof selectFlightWithAlternatives>) {
  const { tour_id, flight_pair_id } = payload;
  /**
   * В зависимости от выбранного перелета, нужно обновить текущую цену
   * (используется для определения необходимости показа сообщения об изменении цены)
   */
  const packagePrice: SagaReturnType<typeof selectCurrentFlightPrice> =
    yield select(selectCurrentFlightPrice, flight_pair_id);
  if (packagePrice) yield put(setCurrentPrice(packagePrice));

  // A/B TESTING
  const { NAME, OPTIONS } = EXPERIMENT_NAMES.ACTUALIZE_ALTERNATIVES;
  const allowAlternatives = getIsFeatureHasAnOption(NAME, OPTIONS.ON);
  if (!allowAlternatives) {
    yield put(selectFlightAction(payload));
    return;
  }
  // A/B TESTING

  const isOriginalTour = isEmpty(yield select(getAltTourById, tour_id));
  if (isOriginalTour) {
    yield put(selectFlightAction(payload));
    return;
  }

  yield put(setIsChangingPackage(true));

  const package_id = yield select(getPackageId);
  const operator_id = yield select(getOperatorId);

  yield put(setPreviousPackageId(package_id));
  yield put(setPreviousOperatorId(operator_id));

  const request_id = yield select(getRequestId);

  try {
    const response: packageDetailsResponse = yield api.getPackagesDetails({
      tour_id,
      request_id,
      source: 'alternatives',
    });

    if (!response.success) {
      throw new Error(`Cannot create new package`);
    }

    const upgradedServicesPriceInfo = yield select(
      servicesToUpgradePriceInfoSelector,
      tour_id,
    );
    yield put(setUpgradedServices(upgradedServicesPriceInfo));

    const origActualizationSucceed = yield select(getActualizationSuccess);
    if (origActualizationSucceed) {
      yield call(moveOriginalTourToAlternatives, tour_id);
    }

    yield put(changePackage(response.package));
    yield take(changePackageIdSuccess.type);
    yield put(selectFlightAction(payload));

    yield put(setIsChangingPackage(false));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
  }
}

/**
 * Сохраняет данные по по основному туру в альтернативные,
 * чтобы при смене пакета среди альтернативных был прошлый основной
 */
function* moveOriginalTourToAlternatives(newTourId: string) {
  const tour_id = yield select(getTourId);
  const booking_url = yield select(getBookingUrl);
  const price = yield select(getNetPrice);
  const extras = yield select(getOriginalPackageExtras);
  const operator_info = yield select(getOperator);
  const fuelfree_bonuses_relative = yield select(
    getOriginalPackageOrderFuelFreeBonuses,
  );
  const labels = yield select(getLabels);
  const instant_confirm = yield select(getInstantConfirm);
  const bonuses = yield select(getOriginalPackageOrderBonuses);

  const medical_insurance = yield select(getMedicalInsurance);
  const transfer = yield select(getTransfer);
  const previous_price = yield select(getPreviousPrice);

  const originalPackage: TourInfo = {
    tour_id,
    booking_url,
    price,
    extras,
    operator_info,
    fuelfree_bonuses_relative,
    previous_price,
    medical_insurance,
    transfer,
    misc_data: {},
    labels,
    instant_confirm,
    bonuses,
  };

  yield put(setAltTour({ tourInfo: originalPackage, tourId: newTourId }));
}

/**
 * Меняет урл при смене packageId
 */
function* handleChangePackageId() {
  const packageId = yield select(getPackageId);
  const path = yield select(getPath);
  const newPath = path.replace(/[0-9]+/, packageId);
  yield put(setAnalytics());
  yield put(replace(newPath));
  yield put(changePackageIdSuccess());
}

/**
 * Запускаем актуализацию соседних рейсов,
 * если клиент вернулся на первый шаг чекаута
 */
function* handleLocationChange() {
  const isAltActualizationDone = yield select(isAltActualizationDoneSelector);
  const isHomePath = yield select(getIsHomePath);

  if (isAltActualizationDone && isHomePath) {
    yield put(setIsAltActualizationDone(false));
    yield call(handleInitAltActualization);
  }
}

function* saga() {
  yield takeLatest(
    [INIT_APP, createPackageSuccess],
    handleInitAltActualization,
  );
  yield takeLatest(fetchAlternatives.type, handleActualizeAlternatives);
  yield takeLatest(
    selectFlightWithAlternatives.type,
    handleSelectFlightWithAlternatives,
  );
  yield takeLatest(changePackage.type, handleChangePackageId);
  yield takeLatest(routeTransitionEndAction, handleLocationChange);
}

export default saga;
